const STATUS_MESSAGES: Partial<Record<number, string>> = {
  0: "Network error. Please check your connection.",
  400: "Invalid request. Please check your input.",
  401: "Your session has expired. Please log in again.",
  403: "You don't have permission to do that.",
  404: "The requested resource was not found.",
  408: "Request timed out. Please try again.",
  409: "This action conflicts with existing data.",
  422: "The provided data is invalid.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "Something went wrong on our end. Please try again.",
};

// Generic HTTP phrases that should be replaced with friendlier messages
const GENERIC_SERVER_PHRASES = new Set([
  "internal server error",
  "bad request",
  "not found",
  "unprocessable entity",
  "forbidden",
  "unauthorized",
  "too many requests",
  "service unavailable",
  "gateway timeout",
  "method not allowed",
  "conflict",
]);

function formatFieldName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/^./, (str) => str.toUpperCase());
}

/**
 * Formats a single class-validator constraint string into a human-readable field error.
 */
function humanizeConstraint(msg: string): string {
  const trimmed = msg.trim();
  if (!trimmed) return "";

  // Pattern: "category must be one of the following values: STANDARD, DELUXE..."
  const enumMatch = trimmed.match(
    /^([a-zA-Z0-9_]+)\s+must be one of the following values:\s*(.+)$/i,
  );
  if (enumMatch) {
    const rawField = enumMatch[1];
    const rawValues = enumMatch[2];
    const fieldName = formatFieldName(rawField);
    const formattedValues = rawValues
      .split(",")
      .map((v) => {
        const val = v.trim();
        return (
          val.charAt(0).toUpperCase() +
          val.slice(1).toLowerCase().replace(/_/g, " ")
        );
      })
      .join(", ");

    return `• ${fieldName}: Invalid selection. Allowed options: ${formattedValues}`;
  }

  // Pattern: "fieldName should not be empty"
  const emptyMatch = trimmed.match(/^([a-zA-Z0-9_]+)\s+should not be empty$/i);
  if (emptyMatch) {
    const fieldName = formatFieldName(emptyMatch[1]);
    return `• ${fieldName}: This field is required`;
  }

  // Pattern: "fieldName must be ..."
  const mustBeMatch = trimmed.match(/^([a-zA-Z0-9_]+)\s+(must .+)$/i);
  if (mustBeMatch) {
    const fieldName = formatFieldName(mustBeMatch[1]);
    return `• ${fieldName}: ${mustBeMatch[2]}`;
  }

  // If already formatted like "Category: Invalid selection"
  if (trimmed.startsWith("• ")) {
    return trimmed;
  }
  if (trimmed.includes(": ")) {
    const parts = trimmed.split(": ");
    const field = formatFieldName(parts[0]);
    return `• ${field}: ${parts.slice(1).join(": ")}`;
  }

  return `• ${trimmed.charAt(0).toUpperCase() + trimmed.slice(1)}`;
}

/**
 * Converts a raw backend response + HTTP status into a human-readable message.
 * Used by apiClient to set ApiError.message at throw time, so every
 * toast.error(e.message) call in the app gets a friendly string for free.
 */
export function parseApiErrorMessage(data: unknown, status: number): string {
  if (data && typeof data === "object") {
    const body = data as Record<string, unknown>;
    const raw = body["message"] ?? body["error"] ?? body["detail"];

    if (Array.isArray(raw) && raw.length > 0) {
      // NestJS class-validator returns an array of constraint messages
      const strings = raw.filter(
        (m): m is string => typeof m === "string" && m.trim().length > 0,
      );
      if (strings.length > 0) {
        const formatted = strings.map(humanizeConstraint);
        const capped = formatted.slice(0, 4).join("\n");
        return formatted.length > 4
          ? `${capped}\n(+${formatted.length - 4} more errors)`
          : capped;
      }
    }

    if (typeof raw === "string" && raw.trim()) {
      const trimmed = raw.trim();
      if (!GENERIC_SERVER_PHRASES.has(trimmed.toLowerCase())) {
        // Handle concatenated sentence errors (e.g. "Category must be... Style must be...")
        if (trimmed.includes("must be one of the following values:")) {
          const sentences = trimmed
            .split(/(?<=\.)\s+/)
            .filter((s) => s.trim().length > 0);
          if (sentences.length > 1) {
            return sentences.map(humanizeConstraint).join("\n");
          }
          return humanizeConstraint(trimmed);
        }
        return trimmed;
      }
    }
  }

  const fallback =
    STATUS_MESSAGES[status] ?? STATUS_MESSAGES[Math.floor(status / 100) * 100];
  return fallback ?? "Something went wrong. Please try again.";
}
